import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { trigger, transition, style, animate, state } from "@angular/animations";
import { Subscription } from "rxjs";
import { ProgramService } from "../core/services/program.service";
import { LanguageService } from "../core/services/language.service";
import { getImageUrl as buildImageUrl } from "../shared/utils/commenFunctions";

type HubTab = 'programs' | 'services' | 'manage-services';

type HubCard = {
  id: string;
  titleKey: string;
  image: string;
  route: string[];
  queryParams?: Record<string, string>;
};

@Component({
  selector: "programs-component, ProgramsComponent",
  standalone: true,
  templateUrl: './programs.component.html',
  styleUrls: ['./programs.component.css'],
  imports: [CommonModule, RouterModule],
  animations: [
    trigger('pageTransition', [
      state('hidden', style({ opacity: 0, visibility: 'hidden' })),
      state('visible', style({ opacity: 1, visibility: 'visible' })),
      transition('hidden => visible', [animate('400ms ease-out')]),
    ]),
    trigger('cardClick', [
      transition('* => clicked', [
        animate('300ms ease-in-out', style({ opacity: 0, transform: 'scale(0.95)' })),
      ]),
    ]),
  ],
})
export class ProgramsComponent implements OnInit, OnDestroy {
  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private languageSub?: Subscription;

  activeTab: HubTab = 'programs';
  staffInfoData: { staffName?: string } | null = null;
  isLoading = true;
  clickedCard: string | null = null;
  imagesLoaded: Record<string, boolean> = {};
  pageTransitionState: 'hidden' | 'visible' = 'visible';
  private cachedProgramImages: Record<string, string> = {};
  private isRedirecting = false;
  private readonly seniorCenterProgramId = 1000;

  private readonly programsCard: HubCard = {
    id: 'senior-center-meal',
    titleKey: 'programs.card.seniorProgram',
    image: 'assets/images/unsplash.jpg',
    route: ['/programs/senior-center'],
  };

  private readonly manageServicesCard: HubCard = {
    id: 'manage-senior-center',
    titleKey: 'programs.card.seniorCenterProgram',
    image: 'assets/images/unsplash.jpg',
    route: ['/manage-services/senior-center'],
  };

  private readonly tabDefs: Array<{ id: HubTab; labelKey: string; disabled: boolean }> = [
    { id: 'programs', labelKey: 'programs.tab.programs', disabled: false },
    { id: 'services', labelKey: 'programs.tab.services', disabled: true },
    { id: 'manage-services', labelKey: 'programs.tab.manageServices', disabled: false },
  ];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly programService: ProgramService,
  ) {}

  get loadingLabel(): string {
    return this.languageService.t('programs.loading');
  }

  get welcomeBefore(): string {
    return this.languageService.t('programs.welcome.before');
  }

  get welcomeAfter(): string {
    return this.languageService.t('programs.welcome.after');
  }

  get areasLabel(): string {
    return this.languageService.t('programs.areasLabel');
  }

  get tabs(): Array<{ id: HubTab; label: string; disabled: boolean }> {
    return this.tabDefs.map((tab) => ({
      id: tab.id,
      disabled: tab.disabled,
      label: this.languageService.t(tab.labelKey),
    }));
  }

  /** Stable card refs — never recreate in a getter (breaks *ngFor clicks). */
  get activeCards(): HubCard[] {
    return this.activeTab === 'manage-services' ? [this.manageServicesCard] : [this.programsCard];
  }

  trackByCardId(_index: number, card: HubCard): string {
    return card.id;
  }

  trackByTabId(_index: number, tab: { id: HubTab }): HubTab {
    return tab.id;
  }

  cardTitle(card: HubCard): string {
    return this.languageService.t(card.titleKey);
  }

  cardOpenLabel(card: HubCard): string {
    return this.languageService.t('programs.openCard', { title: this.cardTitle(card) });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());
    this.loadCachedProgramImages();

    const staffInfo = sessionStorage.getItem('staffInfo');
    if (staffInfo) {
      try {
        this.staffInfoData = JSON.parse(staffInfo);
      } catch {
        this.staffInfoData = null;
      }
    }

    this.imagesLoaded[this.programsCard.id] = false;
    this.imagesLoaded[this.manageServicesCard.id] = false;

    const token = sessionStorage.getItem('token');
    if (!token || !staffInfo) {
      this.redirectToSignIn(this.languageService.t('programs.signInRequired'));
      return;
    }

    this.route.queryParamMap.subscribe((params) => {
      const tab = params.get('tab');
      if (tab === 'manage-services') {
        this.activeTab = 'manage-services';
      } else if (tab === 'programs' || !tab) {
        this.activeTab = 'programs';
      }
    });

    this.loadSeniorCenterCoverImage();
    setTimeout(() => {
      this.isLoading = false;
      this.cdr.markForCheck();
    }, 200);
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  selectTab(tab: HubTab, disabled: boolean): void {
    if (disabled) {
      return;
    }
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: tab === 'programs' ? {} : { tab },
      replaceUrl: true,
    });
  }

  onCardClick(card: HubCard, event: Event): void {
    event.preventDefault();
    this.clickedCard = card.id;
    setTimeout(() => {
      this.router.navigate(card.route, card.queryParams ? { queryParams: card.queryParams } : undefined);
    }, 250);
  }

  getImageUrl(image: string): string {
    if (!image) {
      return '';
    }
    if (this.isLocalAsset(image) || this.isAbsoluteUrl(image)) {
      return this.normalizeAssetPath(image);
    }
    const cached = this.cachedProgramImages[image];
    if (cached) {
      return cached;
    }
    const remoteUrl = buildImageUrl(image);
    this.cachedProgramImages[image] = remoteUrl;
    this.persistCachedImages();
    return remoteUrl;
  }

  onImageLoad(cardId: string): void {
    this.imagesLoaded[cardId] = true;
  }

  onImageError(cardId: string): void {
    this.imagesLoaded[cardId] = true;
  }

  private loadSeniorCenterCoverImage(): void {
    this.programService.getProgramById(this.seniorCenterProgramId).subscribe({
      next: (response) => {
        const coverImageUrl = this.extractSeniorCenterCoverImageUrl(response);
        if (!coverImageUrl) {
          return;
        }
        this.programsCard.image = coverImageUrl;
        this.manageServicesCard.image = coverImageUrl;
        this.imagesLoaded[this.programsCard.id] = false;
        this.imagesLoaded[this.manageServicesCard.id] = false;
        this.cdr.markForCheck();
      },
    });
  }

  private extractSeniorCenterCoverImageUrl(response: unknown): string {
    const root = response as Record<string, unknown> | null | undefined;
    const program = (root?.['program'] ?? root?.['Program']) as Record<string, unknown> | undefined;
    const coverImageUrl = program?.['coverImageUrl'] ?? program?.['CoverImageUrl'];
    return typeof coverImageUrl === 'string' ? coverImageUrl.trim() : '';
  }

  private loadCachedProgramImages(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const cachedImages = sessionStorage.getItem('programImageCache');
    if (!cachedImages) {
      return;
    }
    try {
      this.cachedProgramImages = JSON.parse(cachedImages) ?? {};
    } catch {
      this.cachedProgramImages = {};
    }
  }

  private persistCachedImages(): void {
    if (typeof window === 'undefined') {
      return;
    }
    sessionStorage.setItem('programImageCache', JSON.stringify(this.cachedProgramImages));
  }

  private isLocalAsset(path: string): boolean {
    return path.startsWith('assets/') || path.startsWith('/assets/');
  }

  private isAbsoluteUrl(path: string): boolean {
    return /^https?:\/\//i.test(path) || path.startsWith('data:');
  }

  private normalizeAssetPath(path: string): string {
    return path.startsWith('/') ? path : `/${path}`;
  }

  private redirectToSignIn(errorMessage?: string): void {
    if (this.isRedirecting) {
      return;
    }
    this.isRedirecting = true;
    this.isLoading = true;
    setTimeout(() => {
      this.router.navigate(['/'], {
        replaceUrl: true,
        queryParams: errorMessage ? { error: encodeURIComponent(errorMessage) } : undefined,
      });
    }, 0);
  }
}
